import { useState, type FormEvent } from "react";
import { toKey } from "./editor/shared";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { ApiKey, ApiKeyCreated, type ServiceAccount } from "@workcore/schemas";
import { api, checkedWrite, get } from "../api";
import { useDirectory, useRefresh, useServiceAccounts, wsKey } from "../directory";
import { EmptyState, ErrorBox, Field, PageHeader, Panel, RowMenu, SidePanel, StatusBadge, When, when } from "../ui";
import { HistoryLink } from "../activity";
import { confirmAction } from "../confirm";

function Keys({ slug, sa }: { slug: string; sa: ServiceAccount }) {
  const refresh = useRefresh(slug);
  const keys = useQuery({ queryKey: wsKey(slug, `keys-${sa.id}`), queryFn: async () => z.array(ApiKey).parse(await get(`/v1/ws/${slug}/service-accounts/${sa.id}/keys`)) });
  const [label, setLabel] = useState("");
  const [days, setDays] = useState("90");
  const [secret, setSecret] = useState<string>();
  const create = useMutation({
    // No dry run first: a dry run would mint (and discard) a secret for nothing.
    mutationFn: async () => ApiKeyCreated.parse(await api("POST", `/v1/ws/${slug}/service-accounts/${sa.id}/keys`, { body: { label, ...(days ? { expiresInDays: Number(days) } : {}) } })),
    onSuccess: (k) => (setSecret(k.secret), setLabel(""), refresh()),
  });
  const revoke = useMutation({ mutationFn: (id: number) => api("DELETE", `/v1/ws/${slug}/api-keys/${id}`), onSuccess: refresh });
  return (
    <>
      {secret && (
        <div className="secret" role="status">
          <strong>Copy this key now; it will not be shown again.</strong>
          <code>{secret}</code>
          <span className="actions">
            <button onClick={() => void navigator.clipboard.writeText(secret)}>Copy</button>
            <button className="secondary" onClick={() => setSecret(undefined)}>Done</button>
          </span>
        </div>
      )}
      <form autoComplete="off" className="inline" onSubmit={(e: FormEvent) => (e.preventDefault(), create.mutate())}>
        <label className="inline-field"><span>Label</span><input aria-label="Key label" placeholder="e.g. ci" value={label} onChange={(e) => setLabel(e.target.value)} /></label>
        <label className="inline-field"><span>Expires</span><select aria-label="Expires" value={days} onChange={(e) => setDays(e.target.value)}>
          <option value="30">expires in 30 days</option>
          <option value="90">expires in 90 days</option>
          <option value="365">expires in a year</option>
          <option value="">never expires</option>
        </select></label>
        <button type="submit" disabled={!sa.active || create.isPending}>New key</button>
      </form>
      <ErrorBox error={keys.error ?? create.error ?? revoke.error} />
      {(keys.data?.length ?? 0) > 0 && (
        <table className="grid">
          <thead><tr><th>Key</th><th>Label</th><th>Created</th><th>Last used</th><th>Expires</th><th /></tr></thead>
          <tbody>
            {keys.data?.map((k) => (
              <tr key={k.id} className={k.revokedAt ? "inactive" : ""}>
                <td><code>wk_{slug}_{k.keyPrefix}…</code></td>
                <td>{k.label || "—"}</td>
                <td><When at={k.createdAt} /></td>
                <td><When at={k.lastUsedAt} /></td>
                <td>{k.revokedAt ? <StatusBadge status="revoked" /> : <When at={k.expiresAt} empty="never" />}</td>
                <td className="row-actions">{!k.revokedAt && <RowMenu label={`Actions for key ${k.label}`} items={[{ label: "Revoke…", danger: true, onSelect: () => void confirmAction({ title: `Revoke key '${k.label}'?`, body: `Anything using ${k.keyPrefix}… stops working at once. A revoked key never works again; make a new one if you need it.`, action: "Revoke", typed: k.label, check: () => api("DELETE", `/v1/ws/${slug}/api-keys/${k.id}`, { dryRun: true }) }).then((ok) => ok && revoke.mutate(k.id)) }]} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export function ServiceAccountsPage() {
  const { slug = "" } = useParams();
  const sas = useServiceAccounts(slug);
  const dir = useDirectory(slug);
  const refresh = useRefresh(slug);
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"member" | "guest">("member");
  const [adding, setAdding] = useState(false);
  const create = useMutation({ mutationFn: () => checkedWrite("POST", `/v1/ws/${slug}/service-accounts`, { key, name, role }), onSuccess: () => (setKey(""), setName(""), setAdding(false), refresh()) });
  const toggle = useMutation({ mutationFn: (sa: ServiceAccount) => checkedWrite("PATCH", `/v1/ws/${slug}/service-accounts/${sa.id}`, { active: !sa.active }), onSuccess: refresh });
  return (
    <>
      <PageHeader title="Service accounts" description="Accounts for agents, CI and automation. They hold roles, groups and access like users, and sign in with API keys." actions={<><HistoryLink slug={slug} kind="service-accounts" /><button onClick={() => setAdding(true)}>New service account</button></>} />
      <SidePanel title="New service account" open={adding} onClose={() => setAdding(false)} onSubmit={() => create.mutate()} submitLabel="Create service account" busy={create.isPending} error={create.error}>
        <Field label="Name">
          <input required value={name} autoFocus onChange={(e) => (setName(e.target.value), setKey(toKey(e.target.value).replace(/_/g, "-")))} placeholder="Nightly import" />
        </Field>
        <Field label="Key" help="Part of its identity in the API and logs. It never changes.">
          <input required value={key} onChange={(e) => setKey(e.target.value.toLowerCase())} placeholder="nightly-import" />
        </Field>
        <Field label="Workspace role">
          <select value={role} onChange={(e) => setRole(e.target.value as "member" | "guest")}>
            <option value="member">Member</option>
            <option value="guest">Guest (read and comment only)</option>
          </select>
        </Field>
      </SidePanel>
      <ErrorBox error={sas.error ?? toggle.error} />
      {sas.data?.length === 0 && <EmptyState title="No service accounts yet" body="Give an agent, a CI job or an import script its own account and API key, so its changes are recorded under its name." action={<button onClick={() => setAdding(true)}>New service account</button>} />}
      {sas.data?.map((sa) => (
        <Panel
          key={sa.id}
          title={sa.name}
          actions={
            <span className="actions">
              <code>{sa.key}</code>
              <StatusBadge status={sa.active ? "active" : "inactive"} />
              <RowMenu
                label={`Actions for ${sa.name}`}
                items={[
                  !sa.active && { label: "Reactivate", onSelect: () => toggle.mutate(sa) },
                  sa.active && { label: "Deactivate…", danger: true, onSelect: () => void confirmAction({ title: `Deactivate service account '${sa.name}'?`, body: "Its keys stop working until you reactivate it.", action: "Deactivate", check: () => api("PATCH", `/v1/ws/${slug}/service-accounts/${sa.id}`, { body: { active: false }, dryRun: true }) }).then((ok) => ok && toggle.mutate(sa)) },
                ]}
              />
            </span>
          }
        >
          <p className="muted small">Owned by {dir.label({ kind: "user", id: sa.ownerUserId })} · created {when(sa.createdAt)}</p>
          <Keys slug={slug} sa={sa} />
        </Panel>
      ))}
    </>
  );
}
